import { Component } from '@theme/component';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { compactEaLineProperties, readEaLinePropertiesFromCard, readPresaleLinePropertiesFromCard } from '@theme/ea-cart';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;
  /** @type {SVGElement | null} */
  #activeSvg = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    // Prefer explicit data attribute if provided
    const explicit = this.getAttribute('data-product-url');
    if (explicit) return explicit;
    // Fallback: infer from nearest product-card
    return /** @type {HTMLAnchorElement} */ (
      this.closest('product-card')?.querySelector('a[ref="productCardLink"]')
    )?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const productHandle = this.getAttribute('data-product-handle') || this.#productHandleFromUrl(this.productPageUrl);
    const productTitle = this.getAttribute('data-product-title') || '';

    if (isMobileBreakpoint()) {
      if (productHandle) {
        const eaLineProperties = readEaLinePropertiesFromCard(this);
        document.dispatchEvent(
          new CustomEvent('size-quick-add-modal:open', {
            bubbles: true,
            detail: { handle: productHandle, eaLineProperties },
          })
        );
      }
      return;
    }

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    if (event.target instanceof HTMLButtonElement) {
      const svg = event.target.querySelector('svg');
      if (svg) {
        svg.classList.add('active');
        this.#activeSvg = svg;
      }
    }

    this.#openQuickAddModal();
  };

  /** @param {string} [url] - Product page URL
   * @returns {string|null} Product handle
   */
  #productHandleFromUrl(url) {
    if (!url) return null;
    try {
      const path = new URL(url, window.location.origin).pathname;
      const m = path.match(/\/products\/([^/]+)/);
      return m ? m[1] : null;
    } catch {
      return null;
    }
  }

  /**
   * Opens quick-add modal with a specific variant pre-selected (e.g. from size-quick-add buttons).
   * @param {Event} event - The click event; variant id is read from event target's data-variant-id
   */
  openWithVariant = async (event) => {
    const button = event?.target?.closest?.('[data-variant-id]');
    const variantId = button?.dataset?.variantId;
    const productUrl = button?.dataset?.productUrl || this.getAttribute('data-product-url') || this.productPageUrl;
    if (!variantId || !productUrl) return;

    event.preventDefault();
    event.stopPropagation();

    const separator = productUrl.includes('?') ? '&' : '?';
    const urlWithVariant = `${productUrl}${separator}variant=${variantId}`;

    await this.fetchProductPage(urlWithVariant);

    if (this.#cachedProductHtml) {
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );
      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => {
      this.toggleAttribute('stay-visible', false);
      if (this.#activeSvg) {
        this.#activeSvg.classList.remove('active');
        this.#activeSvg = null;
      }
    }, { once: true });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

/**
 * Wrapper for size-quick-add buttons on product cards. Adds the selected variant
 * directly to the cart instead of opening the quick-add modal.
 */
class SizeQuickAddWrapper extends Component {
  connectedCallback() {
    super.connectedCallback?.();
    this.addEventListener('click', this.#handleClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('click', this.#handleClick, true);
  }

  /**
   * Capture-phase handler so size button clicks never reach the product link (e.g. in collection tabs).
   */
  #handleClick = (event) => {
    const button = event.target?.closest?.('[data-variant-id]');
    if (!button || !this.contains(button) || !(button instanceof HTMLButtonElement) || button.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.addToCart(event);
  };

  /**
   * @param {Event | string} eventOrData - Event (or data segment when called via on:click delegation)
   * @param {Event} [eventParam] - Event when framework passes (data, event)
   */
  addToCart = async (eventOrData, eventParam) => {
    const event = typeof eventOrData?.preventDefault === 'function' ? eventOrData : eventParam;
    if (!event) return;

    const button = event?.target?.closest?.('[data-variant-id]');
    const variantId = button?.dataset?.variantId;
    if (!variantId || !(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    button.setAttribute('aria-busy', 'true');
    button.classList.add('size-quick-add__button--loading');

    try {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset?.sectionId)
        .filter(Boolean);
      const eaRaw = readEaLinePropertiesFromCard(this);
      const eaProps = compactEaLineProperties(eaRaw);
      const presaleProps = readPresaleLinePropertiesFromCard(this);
      const lineProps = { ...eaProps, ...presaleProps };
      const body = {
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            ...(Object.keys(lineProps).length > 0 ? { properties: lineProps } : {}),
          },
        ],
        ...(sectionIds.length > 0 && { sections: sectionIds.join(',') }),
      };
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.status && data.status !== 200) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }

      document.dispatchEvent(
        new CartAddEvent({}, 'size-quick-add', {
          source: 'size-quick-add',
          itemCount: 1,
          variantId: String(variantId),
          sections: data?.sections,
        })
      );

      button.classList.add('size-quick-add__button--added');
      const label = button.querySelector('.size-quick-add__label');
      const previousText = label?.textContent ?? button.textContent ?? '';
      if (label) label.textContent = typeof Theme !== 'undefined' && Theme.translations?.added ? Theme.translations.added : 'Added';
      setTimeout(() => {
        button.classList.remove('size-quick-add__button--added');
        if (label) label.textContent = previousText;
      }, 1600);
    } catch (err) {
      document.dispatchEvent(
        new CartAddEvent({}, 'size-quick-add', { didError: true, source: 'size-quick-add' })
      );
      if (typeof window.Theme !== 'undefined' && window.Theme.translations?.add_to_cart_error) {
        console.warn(window.Theme.translations.add_to_cart_error, err?.message ?? err);
      }
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('size-quick-add__button--loading');
    }
  };
}

if (!customElements.get('size-quick-add-wrapper')) {
  customElements.define('size-quick-add-wrapper', SizeQuickAddWrapper);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    
    const cartDrawer = document.querySelector('cart-drawer-component');
    const willOpenCartDrawer = cartDrawer?.hasAttribute('auto-open');
    
    if (willOpenCartDrawer) {
      // Delay closing to allow cart drawer to open first and maintain scroll-lock
      setTimeout(() => {
        this.closeDialog();
      }, 100);
    } else {
      this.closeDialog();
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
